import CustomBreadcrumbs from "../../components/Tools/Breadcumbs";
import Spinner from "../../components/Tools/spinner";
import SubHeader from "../../components/Subheader/Subheader";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import { Box, Chip, IconButton, Modal, Tooltip } from "@mui/material";
import { Badge } from "react-bootstrap";
import CloseIcon from "@mui/icons-material/Close";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import {
  getGeneratedXmlList,
  getGeneratedXmlDetail,
  pushGeneratedXml,
} from "../../helper/Api";
import { openSecureDocumentOrToast } from "../../helper/secureDocuments";

const toConcatenatedFinYear = (fy) => {
  if (!fy) return "";
  const yearLabel = String(fy.year || "").trim();
  const rangeMatch = yearLabel.match(/^(\d{4})\s*[-/]\s*(\d{2,4})$/);
  if (rangeMatch) {
    const start = rangeMatch[1];
    let end = rangeMatch[2];
    if (end.length === 2) {
      end = `${start.slice(0, 2)}${end}`;
    }
    return `${start}${end}`;
  }
  const start = String(fy.start_year || "").trim();
  let end = String(fy.end_year || "").trim();
  if (start.length === 4 && end.length === 2) {
    end = `${start.slice(0, 2)}${end}`;
  }
  if (start.length === 4 && end.length === 4) {
    return `${start}${end}`;
  }
  return "";
};

const formatIdList = (value) => {
  if (Array.isArray(value) && value.length) {
    return value.join(", ");
  }
  return "-";
};

const pickValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const isPushedRow = (row) => {
  const value = pickValue(row?.is_pushed, row?.is_pushed);
  return value === true || value === 1 || value === "1";
};

const todayDdmmyyyy = () => {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const yyyy = String(now.getFullYear());
  return `${dd}${mm}${yyyy}`;
};

const filenameHasTodayDate = (row) => {
  const filename = String(pickValue(row?.filename, row?.filename) || "");
  return filename.includes(todayDdmmyyyy());
};

const pushButtonState = (row) => {
  if (isPushedRow(row)) {
    return { disabled: true, title: "Already pushed to SFTP" };
  }
  if (!filenameHasTodayDate(row)) {
    return {
      disabled: true,
      title: "Only today's file can be pushed (filename date must be today)",
    };
  }
  return { disabled: false, title: "Push to SFTP" };
};

const XmlGeneratedList = () => {
  const { financialYears } = useSelector((state) => state.authData);
  const fyList = financialYears.payload.fy_list;
  const fyIdRef = useRef(localStorage.getItem("globalDateId"));
  const [fyId, setFyId] = useState(fyIdRef.current);
  const [loading, setLoading] = useState(false);
  const [pushingId, setPushingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [expensesRow, setExpensesRow] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);

  const selectedFy = fyList?.find((fy) => Number(fy.id) === Number(fyId));
  const finYear = toConcatenatedFinYear(selectedFy);

  const handlePageTrigger = () => {
    const nextFyId = localStorage.getItem("globalDateId");
    fyIdRef.current = nextFyId;
    setFyId(nextFyId);
    setPage(1);
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await getGeneratedXmlList({
        page,
        per_page: perPage,
        fin_year: finYear,
      });
      const body = res?.data;
      if (body?.status) {
        setRows(body.result || []);
        setTotalCount(Number(body.count) || 0);
      } else {
        setRows([]);
        setTotalCount(0);
        if (body?.message) {
          toast.error(body.message, { position: toast.POSITION.TOP_CENTER });
        }
      }
    } catch (error) {
      setRows([]);
      setTotalCount(0);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch generated XML list",
        { position: toast.POSITION.TOP_CENTER }
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePushXml = async (row) => {
    const id = row?.id;
    if (!id) {
      toast.error("XML record id not found.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }
    try {
      setPushingId(id);
      const res = await pushGeneratedXml(id);
      const body = res?.data;
      if (body?.status) {
        toast.success(body.message || "XML pushed successfully.", {
          position: toast.POSITION.TOP_CENTER,
        });
        fetchList();
      } else {
        toast.error(body?.message || "Failed to push XML.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to push XML.",
        { position: toast.POSITION.TOP_CENTER }
      );
    } finally {
      setPushingId(null);
    }
  };

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    const n = Number(value);
    if (Number.isNaN(n)) {
      return String(value);
    }
    return n.toLocaleString("en-IN");
  };

  const expenseText = (value) => {
    if (value === null || value === undefined || value === "") {
      return "deleted / not found";
    }
    return value;
  };

  const hasExpenseValue = (getter) =>
    expenses.some((item) => {
      const value = getter(item);
      return value !== null && value !== undefined && value !== "";
    });

  const closeExpensesModal = () => {
    setExpensesOpen(false);
    setExpenses([]);
    setExpensesRow(null);
  };

  const typeLabel = (type) => {
    if (type === "reimbursement") return "Reimbursement";
    if (type === "invoice") return "Invoice";
    return type || "-";
  };

  const handleOpenExpenses = async (row) => {
    if (!row?.id) {
      return;
    }
    setExpensesOpen(true);
    setExpensesRow(row);
    setExpenses([]);
    try {
      setExpensesLoading(true);
      const res = await getGeneratedXmlDetail(row.id);
      const body = res?.data;
      if (body?.status) {
        const result = body.result || row;
        setExpensesRow(result);
        if (Array.isArray(result.expenses)) {
          setExpenses(result.expenses);
        } else {
          const invoices = Array.isArray(result.invoice_ids) ? result.invoice_ids : [];
          const reimbursements = Array.isArray(result.reimbursement_ids)
            ? result.reimbursement_ids
            : [];
          setExpenses([
            ...invoices.map((id) => ({ type: "invoice", id })),
            ...reimbursements.map((id) => ({ type: "reimbursement", id })),
          ]);
        }
      } else {
        toast.error(body?.message || "Record not found", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch XML expenses",
        { position: toast.POSITION.TOP_CENTER }
      );
    } finally {
      setExpensesLoading(false);
    }
  };

  const handleOpenDetail = async (row) => {
    if (!row?.id) {
      return;
    }
    setDetailOpen(true);
    setDetailRow(row);
    try {
      setDetailLoading(true);
      const res = await getGeneratedXmlDetail(row.id);
      const body = res?.data;
      if (body?.status) {
        setDetailRow(body.result || row);
      } else {
        toast.error(body?.message || "Record not found", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch XML detail",
        { position: toast.POSITION.TOP_CENTER }
      );
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [page, perPage, fyId, finYear]);

  const customStyles = {
    headCells: {
      style: {
        fontWeight: "bold",
        fontSize: "14px",
      },
    },
  };

  const columns = [
    {
      name: "Scheme",
      selector: (row) =>
        [pickValue(row.scheme_code, row.scheme_code), pickValue(row.scheme_name, row.scheme_name)]
          .filter(Boolean)
          .join(" — ") || "-",
      sortable: true,
      wrap: true,
      minWidth: "180px",
    },
    {
      name: "File Name",
      cell: (row) => {
        const fileUrl = pickValue(row.file_url, row.file_url);
        const filename = pickValue(row.filename, row.filename) || "Download";
        return fileUrl ? (
          <button
            type="button"
            onClick={() =>
              openSecureDocumentOrToast(fileUrl, {
                download: true,
                fallbackFilename: filename,
              })
            }
            style={{
              border: "none",
              background: "none",
              padding: 0,
              color: "#1d4ed8",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            {filename}
          </button>
        ) : (
          filename || "-"
        );
      },
      wrap: true,
      minWidth: "220px",
    },
    {
      name: "FY",
      selector: (row) => row.fin_year || "-",
      width: "110px",
    },
    {
      name: "Records",
      cell: (row) => {
        const count = row.records ?? 0;
        const canOpen = Boolean(row?.id);
        const badge = (
          <Badge
            bg={canOpen ? "success" : "secondary"}
            pill
            style={{
              minWidth: 28,
              fontSize: 12,
              cursor: canOpen ? "pointer" : "default",
            }}
            onClick={canOpen ? () => handleOpenExpenses(row) : undefined}
          >
            {count}
          </Badge>
        );
        if (!canOpen) {
          return badge;
        }
        return (
          <Tooltip title="View expenses" arrow>
            <span>{badge}</span>
          </Tooltip>
        );
      },
      width: "100px",
    },
    {
      name: "Pushed",
      cell: (row) =>
        isPushedRow(row) ? (
          <Chip
            size="small"
            label="Pushed"
            sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#ecfdf5", color: "#047857" }}
          />
        ) : (
          <Chip
            size="small"
            label="Pending"
            sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#fff7ed", color: "#c2410c" }}
          />
        ),
      width: "110px",
    },
    {
      name: "Created By",
      selector: (row) => row.created_by_name || "-",
      wrap: true,
      minWidth: "150px",
    },
    {
      name: "Department",
      selector: (row) => row.department_name || "-",
      wrap: true,
      minWidth: "140px",
    },
    {
      name: "Created At",
      selector: (row) => row.created_at || row.uploaded_at || "-",
      minWidth: "170px",
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <Tooltip title="View" arrow>
            <IconButton
              size="small"
              onClick={() => handleOpenDetail(row)}
              sx={{
                width: 28,
                height: 28,
                border: "1px solid #1976d2",
                color: "#1976d2",
                borderRadius: "6px",
              }}
            >
              <RemoveRedEyeOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              pushingId === row.id
                ? "Pushing..."
                : pushButtonState(row).title
            }
            arrow
          >
            <span>
              <IconButton
                size="small"
                disabled={pushingId === row.id || pushButtonState(row).disabled}
                onClick={() => handlePushXml(row)}
                sx={{
                  width: 28,
                  height: 28,
                  border: "1px solid #198754",
                  color: "#198754",
                  borderRadius: "6px",
                  opacity: pushButtonState(row).disabled ? 0.45 : 1,
                }}
              >
                {pushingId === row.id ? (
                  <CircularProgress size={14} />
                ) : (
                  <CloudUploadOutlinedIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      ),
      width: "90px",
      ignoreRowClick: true,
    },
  ];

  const showGross = hasExpenseValue(
    (item) => pickValue(item.gross_amount, item.gross_amount, item.amount)
  );
  const showNet = hasExpenseValue((item) => pickValue(item.net_amount, item.net_amount));
  const showPpa = hasExpenseValue((item) => pickValue(item.ppa_number, item.ppa_number));
  const showDepartment = hasExpenseValue((item) => item.department_name);
  const showDate = hasExpenseValue((item) => pickValue(item.expense_date, item.expense_date));
  const thSx = {
    backgroundColor: "#f4f6fb",
    color: "#28326f",
    fontWeight: 700,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #e5e7eb",
    py: 1.25,
  };
  const tdSx = {
    fontSize: 13,
    color: "#1f2937",
    borderBottom: "1px solid #eef1f6",
    py: 1.25,
    verticalAlign: "middle",
  };

  return (
    <div>
      {loading && <Spinner />}
      <SubHeader fy_list={fyList} handlePageTrigger={handlePageTrigger} />
      <div className="row" style={{ padding: "10px 16px" }}>
        <div className="col-md-6">
          <h4 style={{ color: "#28326f", margin: 0 }}>Generated XML List</h4>
          <CustomBreadcrumbs linksArray={[]} title="Generated XML List" />
        </div>
      </div>
      <div className="scheme p-3">
        <DataTable
          customStyles={customStyles}
          className="dataTables_wrapper"
          columns={columns}
          data={rows}
          noDataComponent="No generated XML files found"
          progressPending={loading}
          fixedHeader
          fixedHeaderScrollHeight="600px"
          pagination
          paginationServer
          paginationTotalRows={totalCount}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          onChangePage={(nextPage) => setPage(nextPage)}
          onChangeRowsPerPage={(nextPerPage) => {
            setPerPage(nextPerPage);
            setPage(1);
          }}
        />
      </div>

      <Modal open={expensesOpen} onClose={closeExpensesModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "96vw", md: 880 },
            maxHeight: "88vh",
            bgcolor: "#fff",
            boxShadow: "0 18px 50px rgba(15, 23, 42, 0.28)",
            borderRadius: "12px",
            outline: "none",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              background: "#28326f",
              color: "#fff",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Box sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
                  Expenses in this XML
                </Box>
                <Chip
                  size="small"
                  label={`${expenses.length} expense${expenses.length === 1 ? "" : "s"}`}
                  sx={{
                    height: 22,
                    bgcolor: "rgba(255,255,255,0.16)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 11,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {expensesRow?.filename ? (
                  <Chip
                    size="small"
                    label={expensesRow.filename}
                    sx={{ height: 22, bgcolor: "rgba(255,255,255,0.12)", color: "#fff", fontSize: 11 }}
                  />
                ) : null}
                {expensesRow?.scheme_code ? (
                  <Chip
                    size="small"
                    label={
                      expensesRow.scheme_name
                        ? `${expensesRow.scheme_code} — ${expensesRow.scheme_name}`
                        : expensesRow.scheme_code
                    }
                    sx={{ height: 22, bgcolor: "rgba(255,255,255,0.12)", color: "#fff", fontSize: 11 }}
                  />
                ) : null}
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={closeExpensesModal}
              sx={{ color: "#fff", mt: -0.5 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 0, overflow: "auto", maxHeight: "calc(88vh - 88px)" }}>
            {expensesLoading ? (
              <Box sx={{ py: 6 }}>
                <Spinner />
              </Box>
            ) : expenses.length ? (
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <Box component="thead" sx={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <Box component="tr">
                    <Box component="th" sx={{ ...thSx, width: 48, textAlign: "left", pl: 2.5 }}>#</Box>
                    <Box component="th" sx={{ ...thSx, textAlign: "left" }}>Type</Box>
                    <Box component="th" sx={{ ...thSx, textAlign: "left" }}>Voucher / Order No</Box>
                    <Box component="th" sx={{ ...thSx, textAlign: "left" }}>Beneficiary</Box>
                    {showGross ? (
                      <Box component="th" sx={{ ...thSx, textAlign: "right" }}>Gross</Box>
                    ) : null}
                    {showNet ? (
                      <Box component="th" sx={{ ...thSx, textAlign: "right" }}>Net</Box>
                    ) : null}
                    {showPpa ? (
                      <Box component="th" sx={{ ...thSx, textAlign: "left" }}>PPA</Box>
                    ) : null}
                    {showDepartment ? (
                      <Box component="th" sx={{ ...thSx, textAlign: "left" }}>Department</Box>
                    ) : null}
                    {showDate ? (
                      <Box component="th" sx={{ ...thSx, textAlign: "left", pr: 2.5 }}>Date</Box>
                    ) : null}
                  </Box>
                </Box>
                <Box component="tbody">
                  {expenses.map((item, index) => (
                    <Box
                      component="tr"
                      key={`${item.type}-${item.id}-${index}`}
                      sx={{
                        "&:hover td": { backgroundColor: "#f8fafc" },
                        "&:nth-of-type(even) td": { backgroundColor: "#fafbfd" },
                      }}
                    >
                      <Box component="td" sx={{ ...tdSx, pl: 2.5, color: "#6b7280" }}>
                        {index + 1}
                      </Box>
                      <Box component="td" sx={tdSx}>
                        <Chip
                          size="small"
                          label={typeLabel(item.type)}
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 600,
                            bgcolor: item.type === "reimbursement" ? "#ecfdf5" : "#eff6ff",
                            color: item.type === "reimbursement" ? "#047857" : "#1d4ed8",
                          }}
                        />
                      </Box>
                      <Box component="td" sx={{ ...tdSx, fontWeight: 600 }}>
                        {expenseText(pickValue(item.voucher_no, item.invoice_number))}
                      </Box>
                      <Box component="td" sx={tdSx}>
                        {expenseText(item.beneficiary_name)}
                      </Box>
                      {showGross ? (
                        <Box component="td" sx={{ ...tdSx, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {formatAmount(pickValue(item.gross_amount, item.gross_amount, item.amount))}
                        </Box>
                      ) : null}
                      {showNet ? (
                        <Box component="td" sx={{ ...tdSx, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {formatAmount(pickValue(item.net_amount, item.net_amount))}
                        </Box>
                      ) : null}
                      {showPpa ? (
                        <Box component="td" sx={tdSx}>{pickValue(item.ppa_number, item.ppa_number) || "-"}</Box>
                      ) : null}
                      {showDepartment ? (
                        <Box component="td" sx={tdSx}>{item.department_name || "-"}</Box>
                      ) : null}
                      {showDate ? (
                        <Box component="td" sx={{ ...tdSx, pr: 2.5 }}>
                          {pickValue(item.expense_date, item.expense_date) || "-"}
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Box sx={{ py: 6, textAlign: "center", color: "#6b7280", fontSize: 14 }}>
                No expenses found for this XML
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      <Modal open={detailOpen} onClose={() => setDetailOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92vw", sm: 520 },
            bgcolor: "#fff",
            boxShadow: "0 18px 50px rgba(15, 23, 42, 0.28)",
            borderRadius: "12px",
            outline: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              background: "#28326f",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ fontSize: 18, fontWeight: 700 }}>Generated XML Detail</Box>
            <IconButton size="small" onClick={() => setDetailOpen(false)} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 2.5 }}>
            {detailLoading ? (
              <Spinner />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, fontSize: 13, color: "#1f2937" }}>
                <div><strong>Scheme:</strong> {pickValue(detailRow?.scheme_code, detailRow?.scheme_code)} {pickValue(detailRow?.scheme_name, detailRow?.scheme_name) || ""}</div>
                <div><strong>File:</strong> {pickValue(detailRow?.filename, detailRow?.filename) || "-"}</div>
                <div><strong>FY:</strong> {detailRow?.fin_year || "-"}</div>
                <div><strong>Records:</strong> {detailRow?.records ?? "-"}</div>
                <div><strong>Pushed:</strong> {isPushedRow(detailRow) ? `Yes (${pickValue(detailRow?.pushed_at, detailRow?.pushed_at) || "-"})` : "No"}</div>
                <div><strong>Created by:</strong> {detailRow?.created_by_name || "-"} ({detailRow?.created_by_email || "-"})</div>
                <div><strong>Department:</strong> {detailRow?.department_name || "-"}</div>
                <div><strong>Invoice IDs:</strong> {formatIdList(detailRow?.invoice_ids)}</div>
                <div><strong>Reimbursement IDs:</strong> {formatIdList(detailRow?.reimbursement_ids)}</div>
                <div><strong>Created at:</strong> {detailRow?.created_at || detailRow?.uploaded_at || "-"}</div>
                {pickValue(detailRow?.file_url, detailRow?.file_url) ? (
                  <button
                    type="button"
                    onClick={() =>
                      openSecureDocumentOrToast(pickValue(detailRow?.file_url, detailRow?.file_url), {
                        download: true,
                        fallbackFilename: pickValue(detailRow?.filename, detailRow?.filename) || "file.xml",
                      })
                    }
                    style={{
                      border: "none",
                      background: "none",
                      padding: 0,
                      color: "#1d4ed8",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    Open / Download XML
                  </button>
                ) : null}
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default XmlGeneratedList;
